// ManualDestinationsEditor — inline editor used in the Publisher page header
// and (mirrored) in Settings → Integrations. Plain CRUD over
// `workspace_manual_destinations` for the active workspace, plus a per-row
// content-type popover that controls which Publish Types this destination
// chip appears on.

import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Settings2, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  type ManualDestination,
  createManualDestination,
  deleteManualDestination,
  listManualDestinations,
  renameManualDestination,
  updateManualDestinationAppliesTo,
} from "@/lib/workspaceManualDestinations";
import { PUBLISH_TYPES, type PublishTypeKey } from "@/lib/publishTypes";

type Props = {
  workspaceId: string;
  userId: string;
  /** Notified when the list changes so callers can refresh chip rows. */
  onChanged?: () => void;
};

const AppliesToPopover = ({
  row,
  onSaved,
}: {
  row: ManualDestination;
  onSaved: () => void;
}) => {
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<Set<PublishTypeKey>>(
    () => new Set(row.applies_to_publish_types),
  );
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setSelected(new Set(row.applies_to_publish_types));
  }, [open, row.applies_to_publish_types]);

  const toggle = (key: PublishTypeKey, next: boolean) => {
    setSelected((prev) => {
      const copy = new Set(prev);
      if (next) copy.add(key);
      else copy.delete(key);
      return copy;
    });
  };

  const handleSave = async () => {
    if (selected.size === 0) {
      toast.error("Pick at least one content type.");
      return;
    }
    setSaving(true);
    try {
      await updateManualDestinationAppliesTo(row.id, Array.from(selected));
      onSaved();
      setOpen(false);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Update failed");
    } finally {
      setSaving(false);
    }
  };

  const count = row.applies_to_publish_types.length;
  const allCount = PUBLISH_TYPES.length;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          size="sm"
          variant="ghost"
          className="h-8 gap-1 px-2 text-[11px] text-muted-foreground"
          title="Content types this chip applies to"
        >
          <Settings2 className="h-3.5 w-3.5" />
          {count === allCount ? "All types" : `${count}/${allCount}`}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-64 space-y-2 p-3">
        <p className="text-xs font-medium">Show chip for…</p>
        <div className="space-y-1.5">
          {PUBLISH_TYPES.map((pt) => (
            <label
              key={pt.key}
              className="flex items-start gap-2 rounded-sm px-1 py-0.5 text-xs hover:bg-muted/50"
            >
              <Checkbox
                checked={selected.has(pt.key)}
                onCheckedChange={(v) => toggle(pt.key, v === true)}
                className="mt-0.5"
              />
              <span className="leading-tight">{pt.label}</span>
            </label>
          ))}
        </div>
        <div className="flex justify-end gap-1.5 pt-1">
          <Button size="sm" variant="ghost" className="h-7" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button size="sm" className="h-7" disabled={saving} onClick={() => void handleSave()}>
            Save
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export const ManualDestinationsEditor = ({ workspaceId, userId, onChanged }: Props) => {
  const [rows, setRows] = useState<ManualDestination[]>([]);
  const [loading, setLoading] = useState(true);
  const [newLabel, setNewLabel] = useState("");
  const [busyId, setBusyId] = useState<string | null>(null);

  const refresh = async () => {
    setLoading(true);
    try {
      setRows(await listManualDestinations(workspaceId));
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to load destinations");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId]);

  const handleAdd = async () => {
    const label = newLabel.trim();
    if (!label) return;
    try {
      await createManualDestination({
        workspaceId,
        label,
        position: rows.length,
        createdByUserId: userId,
      });
      setNewLabel("");
      await refresh();
      onChanged?.();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Add failed");
    }
  };

  const handleRename = async (id: string, label: string) => {
    setBusyId(id);
    try {
      await renameManualDestination(id, label);
      await refresh();
      onChanged?.();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Rename failed");
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (id: string) => {
    setBusyId(id);
    try {
      await deleteManualDestination(id);
      await refresh();
      onChanged?.();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Delete failed");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="space-y-2">
      <p className="text-xs text-muted-foreground">
        Manual destinations are copy-and-paste targets. Each shows up as a chip on every
        publication whose content type matches the destination's settings.
      </p>
      <div className="space-y-1.5">
        {loading ? (
          <p className="text-xs text-muted-foreground">Loading…</p>
        ) : rows.length === 0 ? (
          <p className="text-xs text-muted-foreground">No manual destinations yet.</p>
        ) : (
          rows.map((row) => (
            <div key={row.id} className="flex items-center gap-2">
              <Input
                defaultValue={row.label}
                disabled={busyId === row.id}
                onBlur={(e) => {
                  const next = e.currentTarget.value.trim();
                  if (next && next !== row.label) void handleRename(row.id, next);
                }}
                className="h-8 text-sm"
              />
              <AppliesToPopover
                row={row}
                onSaved={() => {
                  void refresh();
                  onChanged?.();
                }}
              />
              <Button
                size="sm"
                variant="ghost"
                className="h-8 w-8 p-0"
                disabled={busyId === row.id}
                onClick={() => void handleDelete(row.id)}
                title="Remove"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </div>
          ))
        )}
      </div>
      <div className="flex items-center gap-2 pt-1">
        <Input
          placeholder="Add destination (e.g. Reddit)"
          value={newLabel}
          onChange={(e) => setNewLabel(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              void handleAdd();
            }
          }}
          className="h-8 text-sm"
        />
        <Button size="sm" variant="outline" className="h-8 gap-1" onClick={() => void handleAdd()}>
          <Plus className="h-3.5 w-3.5" /> Add
        </Button>
      </div>
    </div>
  );
};
